import { formatServiceArea, serviceAreaNumber } from '@/components/ui';

export interface LegendEntry {
  /** Foreman area name ("(Blank)" when the Work ID has none). */
  name: string;
  color: string;
  /** Service area name or code of the foreman area, if known. */
  serviceArea?: string | number | null;
}

export interface LegendGroup {
  /** "SA16", or "Other" for foreman areas with no known service area. */
  label: string;
  /** Full name for the group heading's tooltip ("Service Area 16"). */
  title: string;
  items: LegendEntry[];
}

const collator = new Intl.Collator('en-US', { sensitivity: 'base', numeric: true });

/**
 * Work IDs map legend (W-M15): foreman areas grouped by service area, groups in service area
 * order (SA16, SA17, ...), names A–Z within each group. Unknown service area goes last.
 */
export function groupLegendByServiceArea(entries: LegendEntry[]): LegendGroup[] {
  const groups = new Map<string, LegendGroup & { sortKey: number }>();
  for (const e of entries) {
    const label = formatServiceArea(e.serviceArea ?? null, true) ?? 'Other';
    let g = groups.get(label);
    if (!g) {
      const n = serviceAreaNumber(e.serviceArea ?? null);
      g = {
        label,
        title: formatServiceArea(e.serviceArea ?? null, false) ?? 'No service area',
        items: [],
        sortKey: label === 'Other' ? Number.MAX_SAFE_INTEGER : n ? Number(n) : Number.MAX_SAFE_INTEGER - 1,
      };
      groups.set(label, g);
    }
    if (!g.items.some((i) => i.name === e.name)) g.items.push(e);
  }
  return Array.from(groups.values())
    .sort((a, b) => a.sortKey - b.sortKey || collator.compare(a.label, b.label))
    .map(({ sortKey: _sortKey, ...g }) => ({
      ...g,
      items: [...g.items].sort((a, b) => collator.compare(a.name, b.name)),
    }));
}
